import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  List,
  ListItem,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { UTurnRight } from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import MenuItemComponent from "./MenuItemComponent";

const MenuListButton = styled("div")(({ theme }) => ({
  opacity: "0.7",
  transition: "0.3s",
  fontWeight: "400",
  margin: "5px 8px",
  borderRadius: "10px",
  color: theme.palette.primary.light,
  cursor: "pointer",
  padding: "12px 20px",
  display: "flex",
  alignItems: "center",
  textDecoration: "none",
  "& svg": {
    color: theme.palette.primary.light,
    fontSize: "22px",
  },
  "& span": {
    fontWeight: "600",
    fontSize: "15px",
  },
  "&:hover": {
    opacity: "1",
  },
  "&:active": {
    opacity: "1",
  },
  "&:focus": {
    opacity: "1",
  },
}));

export default function MenuItems({ menuList, isStaticRoute = false, open }) {
  const location = useLocation();
  const [currMenuName, setCurrMenuName] = useState("");
  const [currSubMenuName, setCurrSubMenuName] = useState("");
  const [menuLevelOpen, setMenuLevelOpen] = useState(false);
  const [subMenuLevelOpen, setSubMenuLevelOpen] = useState(false);

  const handleMenuDown = (name) => {
    setCurrMenuName(name);
    if (name !== currMenuName) {
      setMenuLevelOpen(true);
    } else if (name === currMenuName) {
      setMenuLevelOpen((prev) => !prev);
    } else {
      setMenuLevelOpen(false);
    }
  };

  const handleSubMenuDown = (name) => {
    setCurrSubMenuName(name);
    if (name !== currSubMenuName) {
      setSubMenuLevelOpen(true);
    } else if (name === currSubMenuName) {
      setSubMenuLevelOpen((prev) => !prev);
    } else {
      setSubMenuLevelOpen(false);
    }
  };

  // Keep the expanded/highlighted menu in sync with the current route,
  // so navigating here programmatically (e.g. from a Dashboard card click)
  // opens the right menu/submenu instead of leaving everything collapsed.
  useEffect(() => {
    for (const menu of menuList || []) {
      if (!menu.sub_menu) continue;

      for (const sub of menu.sub_menu) {
        const nested = sub.children || sub.sub_menu || [];
        const nestedMatch = nested.find(
          (child) => child.path === location.pathname
        );

        if (sub.path === location.pathname || nestedMatch) {
          setCurrMenuName(menu.name);
          setMenuLevelOpen(true);

          if (nestedMatch) {
            setCurrSubMenuName(sub.name);
            setSubMenuLevelOpen(true);
          }
          return;
        }
      }
    }
  }, [location.pathname, menuList]);

  // If it's a static route, show a back button to return to main app
  if (isStaticRoute) {
    return (
      <List>
        <ListItem disablePadding sx={{ display: "block" }}>
          <Link to="/" style={{ textDecoration: "none" }}>
            <MenuListButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
                background: "transparent",
                opacity: 1,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 1.6 : "auto",
                  justifyContent: "center",
                }}
              >
                <UTurnRight />
              </ListItemIcon>
              <ListItemText
                primary="Back to Main App"
                sx={{ opacity: open ? 1 : 0 }}
              />
            </MenuListButton>
          </Link>
        </ListItem>
        <Divider />
        {menuList.map((list, index) => (
          <MenuItemComponent
            key={`${list.name}-${index}`}
            item={list}
            open={open}
            location={location}
            currMenuName={currMenuName}
            currSubMenuName={currSubMenuName}
            menuLevelOpen={menuLevelOpen}
            subMenuLevelOpen={subMenuLevelOpen}
            onMenuClick={handleMenuDown}
            onSubMenuClick={handleSubMenuDown}
          />
        ))}
      </List>
    );
  }

  return (
    <List>
      {menuList.map((list, index) => (
        <MenuItemComponent
          key={`${list.name}-${index}`}
          item={list}
          open={open}
          location={location}
          currMenuName={currMenuName}
          currSubMenuName={currSubMenuName}
          menuLevelOpen={menuLevelOpen}
          subMenuLevelOpen={subMenuLevelOpen}
          onMenuClick={handleMenuDown}
          onSubMenuClick={handleSubMenuDown}
        />
      ))}
    </List>
  );
}
